import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie';
import { NEVER, Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import * as URI from 'urijs';

import { AuthServiceLogin } from '®shared/auth/auth-service-login';
import { AppConfig } from '®shared/config';
import { RedirectionService } from '®shared/redirection';

import { AuthProvider } from './auth-provider';
import { AuthenticationFailedException } from './authentication-failed-exception';
import { PersonaProfile } from './persona-profile';
import { PersonaUser } from './persona-user';
import { User } from './user';
import { UserScopesService } from './user-scopes.service';

const PERSONA_JSONP_CALLBACK = 'cb';

@Injectable()
export class PersonaAuthService
  extends AuthProvider<User>
  implements AuthServiceLogin
{
  // Base URL for all Persona endpoints
  personaBaseUrl: URI;
  // Base URL for Persona endpoints relative to the tenant
  personaBaseWithTenant: URI;
  // URL for logging in through the tenant
  personaLoginAtTenantUrl: URI;
  // URL for logging in through the Talis login provider
  personaTalisUserLoginUrl: URI;

  constructor(
    appConfig: AppConfig,
    private cookieService: CookieService,
    private httpClient: HttpClient,
    redirectionService: RedirectionService,
    userScopesService: UserScopesService,
  ) {
    super(appConfig, redirectionService, userScopesService);

    // versioned Persona URL
    this.personaBaseUrl = new URI(this.appConfig.personaEndpoint);

    // Persona tenancy authentication base URL
    const tenantCode = this.appConfig.tenant.shortCode;
    this.personaBaseWithTenant = this.personaBaseUrl
      .clone()
      .segment(`auth/providers/${tenantCode}`);

    // URL for user to manually log in (with redirection)
    this.personaLoginAtTenantUrl = this.personaBaseWithTenant
      .clone()
      .segment('login');

    // URL for Talis login
    const talisLoginProvider = this.appConfig.talisLoginProvider;
    this.personaTalisUserLoginUrl = this.personaBaseUrl
      .clone()
      .segment(`auth/providers/${talisLoginProvider}/login`);
  }

  /**
   * Returns a Persona URI for fetching data (which does not include any login provider)
   * May also include a doubleSubmit cookie value if it is set
   * @returns {URI} - a Persona URI for fetching data
   **/
  get personaGetUserDataUrl(): URI {
    return this.personaBaseUrl
      .clone()
      .segment('auth/login.json')
      .search({ doubleSubmit: this.cookieService.get('doubleSubmit') });
  }
  /**
   * Creates a Persona authentication URL, with a redirect.
   *
   * @param {string} redirectUri - Redirect to this URL following successful authentication
   * @returns {string} - a Persona URL for logging in.
   **/
  authenticateUrl(redirectUri): string {
    return this.personaLoginAtTenantUrl
      .clone()
      .search({ redirectUri })
      .toString();
  }

  /**
   * Creates a Persona authentication URL using Talis login, with a redirect.
   *
   * @param {string} redirectUri - Redirect to this URL following successful authentication
   * @returns {string} - a Persona URL for logging in.
   **/
  authenticateAsTalisUrl(redirectUri): string {
    return this.personaTalisUserLoginUrl
      .clone()
      .search({ redirectUri })
      .toString();
  }

  /**
   * Ensures that a given URL is absolute, so that it can be used in redirection.
   *
   * @param {string?} url - An absolute or relative URL.
   * @returns {string?} - an absolute version of the source URL.
   **/
  ensureAbsoluteUrl(url: string): string {
    // non-strings cannot be URLs
    if (!url || !url.trim()) {
      return undefined;
    }

    // non-URLs cannot be made absolute
    const uri = new URI(url);
    if (!uri.is('url')) {
      return undefined;
    }

    // absolute URLs get returned as-is
    if (uri.is('absolute')) {
      return uri.toString();
    }

    // relative URLs get converted to absolute
    return uri.absoluteTo(this.getBaseUrl()).toString();
  }

  /**
   * Returns the base URL for the site. Separated for unit-testing.
   *
   * @returns {URI} - an absolute base URL for this site.
   **/
  getBaseUrl(): URI {
    return new URI();
  }

  /**
   * Creates a URL that redirects a specified URL through Persona.
   *
   * @param {string} url - An absolute or relative URL.
   * @param {boolean} isTalisLogin - when true, use Talis login; when false, use provider login
   * @returns {string} - a Persona redirection URL, arriving back at the specified URL.
   **/
  personaRedirectionUri(url: string, isTalisLogin: boolean): string {
    const absolute = this.ensureAbsoluteUrl(url);
    if (!absolute) {
      return undefined;
    }

    return isTalisLogin
      ? this.authenticateAsTalisUrl(absolute)
      : this.authenticateUrl(absolute);
  }

  /**
   * Fetch user profile data from the Persona API via JSONP.
   *
   * @returns {Observable<PersonaUser>} - user data retrieved from Persona
   **/
  getProfileFromPersona(): Observable<PersonaUser> {
    const handleError = error => {
      return observableThrowError(
        new AuthenticationFailedException(
          (error && error.message) || 'Unknown authentication error',
        ),
      );
    };

    const loginUrl = this.personaGetUserDataUrl.toString();

    return this.httpClient
      .jsonp<PersonaProfile>(loginUrl, PERSONA_JSONP_CALLBACK)
      .pipe(
        map(loginResponse => new PersonaUser(loginResponse)),
        map(personaUser => {
          if (
            !this.userScopesService.isAuthorizedAsMemberAtTenant(personaUser)
          ) {
            throw new AuthenticationFailedException(
              'Persona User from another tenancy',
            );
          }
          return personaUser;
        }),
        catchError(handleError),
      );
  }

  /**
   * Get the user from the current identity provider
   *
   * @returns User - the user, if they are authenticated
   **/
  public getCurrentToken(_returnUrl?: string): Observable<User> {
    return this.getProfileFromPersona();
  }

  /**
   * Reauthenticate the user (unless currently in the process of reauthenticating)
   *
   * @returns Observable<User> - the user profile, once authenticated
   **/
  public refreshToken(): Observable<User> {
    return this.getProfileFromPersona();
  }

  /**
   * Perform reauthentication with Persona.
   *
   * If the bearer token has expired, this will attempt to use the refresh token. If the refresh token
   * has expired, a full redirect-through-Persona flow is employed to reauthenticate the user and reload
   * the current page.
   *
   * Note that this observable will never resolve because we use a redirect-through-external-URL strategy
   *
   * @return {Observable<User>} - A User. This will *never* resolve.
   */
  public obtainNewToken(returnUrl?: string): Observable<User> {
    const currentUri = returnUrl ? new URI(returnUrl) : this.getCurrentUri();
    const isTalisLogin = this.isTalisLoginUri(currentUri);

    return this.handlePersonaRefreshTokenReauthentication(
      currentUri.toString(),
      isTalisLogin,
    );
  }

  public loginDuringRouteNavigation(stateUrl: string): void {
    const urlBase = this.getCurrentUri().origin();
    const returnUrl = new URL(stateUrl, urlBase).toString();

    this.handlePersonaRefreshTokenReauthentication(returnUrl, false);
  }

  public login(): void {
    const returnUrl = this.getCurrentUri().toString();

    this.handlePersonaRefreshTokenReauthentication(returnUrl, false);
  }

  /**
   * Performs a Persona refresh token reauthentication.
   *
   * Note that this observable will never resolve because we use a redirect-through-external-URL strategy
   *
   * @param {string}  url          The URL being accessed
   * @param {boolean} isTalisLogin when true, use Talis login; when false, use provider login
   * @return {Observable<User>} - A User. This will *never* resolve.
   */
  private handlePersonaRefreshTokenReauthentication(
    url: string,
    isTalisLogin: boolean,
  ): Observable<User> {
    this.redirectionService.redirectToExternalUrl(
      this.personaRedirectionUri(url, isTalisLogin),
    );
    return NEVER;
  }
}
